const clampChannel = (value) => Math.max(0, Math.min(255, Math.round(value)));

const parseColor = (value) => {
  const input = String(value || '').trim();

  if (!input) {
    return { r: 15, g: 23, b: 42 };
  }

  const normalizedHex = input.replace('#', '');
  if (/^[0-9a-fA-F]{6}$/.test(normalizedHex)) {
    return {
      r: parseInt(normalizedHex.slice(0, 2), 16),
      g: parseInt(normalizedHex.slice(2, 4), 16),
      b: parseInt(normalizedHex.slice(4, 6), 16)
    };
  }

  const rgbMatch = input.match(/^rgba?\((\d+),\s*(\d+),\s*(\d+)/i);
  if (rgbMatch) {
    return {
      r: Number(rgbMatch[1]),
      g: Number(rgbMatch[2]),
      b: Number(rgbMatch[3])
    };
  }

  return { r: 15, g: 23, b: 42 };
};

export const mixHexColor = (sourceColor, targetColor, amount = 0.5) => {
  const source = parseColor(sourceColor);
  const target = parseColor(targetColor);
  const ratio = Math.max(0, Math.min(1, amount));

  return `rgb(${clampChannel(source.r + (target.r - source.r) * ratio)}, ${clampChannel(source.g + (target.g - source.g) * ratio)}, ${clampChannel(source.b + (target.b - source.b) * ratio)})`;
};

export const withAlpha = (color, alpha = 1) => {
  const { r, g, b } = parseColor(color);
  return `rgba(${r}, ${g}, ${b}, ${Math.max(0, Math.min(1, alpha))})`;
};

const getStableHash = (value) => {
  const normalized = String(value || 'default').trim().toLowerCase();
  let hash = 0;

  for (let i = 0; i < normalized.length; i += 1) {
    hash = ((hash << 5) - hash) + normalized.charCodeAt(i);
    hash |= 0;
  }

  return Math.abs(hash);
};

const hslToHex = (h, s, l) => {
  const hue = ((h % 360) + 360) % 360;
  const sat = Math.max(0, Math.min(100, s)) / 100;
  const lig = Math.max(0, Math.min(100, l)) / 100;

  const c = (1 - Math.abs(2 * lig - 1)) * sat;
  const x = c * (1 - Math.abs((hue / 60) % 2 - 1));
  const m = lig - c / 2;

  let r = 0;
  let g = 0;
  let b = 0;

  if (hue < 60) {
    r = c; g = x; b = 0;
  } else if (hue < 120) {
    r = x; g = c; b = 0;
  } else if (hue < 180) {
    r = 0; g = c; b = x;
  } else if (hue < 240) {
    r = 0; g = x; b = c;
  } else if (hue < 300) {
    r = x; g = 0; b = c;
  } else {
    r = c; g = 0; b = x;
  }

  const toHex = (value) => clampChannel((value + m) * 255).toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

const buildGeneratedColor = (seed) => {
  const hash = getStableHash(seed || 'default');
  const hue = hash % 360;
  const saturation = 64 + (hash % 10);
  const lightness = 44 + ((hash >> 3) % 8);
  return hslToHex(hue, saturation, lightness);
};

const buildSoftColorFromHex = (hex) => mixHexColor(hex, '#ffffff', 0.84);

const buildIdentitySeed = (source = {}) => {
  const primaryIdentity =
    source?.transporterSlug ||
    source?.slug ||
    source?.transporterId ||
    source?.id ||
    source?.driverName ||
    source?.choferName ||
    source?.transporterName ||
    source?.name ||
    'default';

  return [
    source?.companyId,
    primaryIdentity,
    source?.transportType || source?.transport
  ]
    .filter(Boolean)
    .join('::');
};

export const resolveTransportIdentity = (source = {}) => {
  const explicitColor =
    source?.identityColor ||
    source?.transporterColor ||
    source?.transportColor ||
    source?.color ||
    null;

  const explicitSoftColor =
    source?.identitySoftColor ||
    source?.transporterSoftColor ||
    source?.transportSoftColor ||
    source?.softColor ||
    null;

  const identitySeed = buildIdentitySeed(source);

  if (explicitColor) {
    return {
      identityColor: explicitColor,
      identitySoftColor: explicitSoftColor || buildSoftColorFromHex(explicitColor),
      identitySeed,
      isPersisted: true
    };
  }

  const generatedColor = buildGeneratedColor(identitySeed);

  return {
    identityColor: generatedColor,
    identitySoftColor: buildSoftColorFromHex(generatedColor),
    identitySeed,
    isPersisted: false
  };
};
